import React from 'react';
import {
  Panel, Badge, Button, Heading,
} from '@printdeal/ui-library';
import { Link as GatsbyLink } from 'gatsby';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { LineItemStates } from '@printdeal/business-logic';
import { SanitizedLineItem, SanitizedOrder } from '../../../types/order/Order';
import useReOrder from '../../my-account/components/shared/Actions/ReOrderAction/useReOrder';
import { Money } from '../../Money';
import { messages as lineItemMessages } from '../../my-account/translations/lineItems';
import {
  recentOrderMessages,
  getDiscountedPrice,
  getRecentOrderStatus,
  recentOrderEvents,
  getOrderAge,
} from './RecentOrderHelper';
import { RecentOrderReOrderLink } from './RecentOrderReOrderLink';

interface RecentOrderItemPanelProps {
  lineItem: SanitizedLineItem;
  order: SanitizedOrder;
  position: string;
}

export const RecentOrderItemPanel = ({
  lineItem, order, position,
}: RecentOrderItemPanelProps) => {
  const intl = useIntl();
  const canReOrder = useReOrder([lineItem], order, true);
  const stateName = getRecentOrderStatus(lineItem);
  if (!stateName) return null;
  const lowerCaseStateName = stateName?.toLowerCase() as Lowercase<LineItemStates>;
  const deliveryStatusMessage = lineItemMessages[lowerCaseStateName] || lineItemMessages.unknown;
  /**
   * Hiding Expected delivery info for line item order state `cancelled`
   * We are showing `Order Completed on <Date>`, if the over all order status is `complete`
   */
  const hideDeliveryInfoLineItem = lowerCaseStateName === 'cancelled';
  const orderStatusLowerCased = order.orderState.toLowerCase() === 'complete';

  const handleOrderDetailsClick = () => {
    addDataLayerEntry(
      recentOrderEvents.RECENT_ORDER_CLICK,
      {
        position,
        button: 'order_details',
        stateName,
        orderAge: getOrderAge(new Date(lineItem?.sane.finalDeliveryDate)),
      },
    );
  };

  return (
    <Panel
      as="article"
      className="pd-flex pd-flex-col pd-gap-3 pd-p-6"
    >
      <ul>
        <li className="pd-mb-2 pd-flex pd-flex-row pd-items-start pd-justify-between pd-gap-4 pd-font-semibold">
          <Heading variant="display" as="h3">{lineItem?.sane.productName},{' '}
            <FormattedMessage
              id={recentOrderMessages.recentOrderProductQuantity.id}
              defaultMessage="{quantity} {quantity, plural, one {piece} other {pieces}}"
              values={{ quantity: lineItem?.quantity }}
            />
          </Heading>
          <p className="pd-text-md pd-leading-7">
            <Money value={getDiscountedPrice(lineItem, order)} />
          </p>
        </li>
        <Badge variant={deliveryStatusMessage.color} className="pd-mb-2">
          {intl.formatMessage(deliveryStatusMessage)}
        </Badge>
        {!hideDeliveryInfoLineItem && (
          <li className="pd-mb-2 pd-text-sm pd-text-dim-600">
            {orderStatusLowerCased ? (
              <FormattedMessage
                id={recentOrderMessages.recentOrderCompletedText.id}
                defaultMessage={recentOrderMessages.recentOrderCompletedText.defaultMessage}
              />
            ) : (
              <FormattedMessage
                id={recentOrderMessages.recentOrderDeliveryDate.id}
                defaultMessage={recentOrderMessages.recentOrderDeliveryDate.defaultMessage}
              />
            )}
            {' '}
            <time dateTime={lineItem?.sane.finalDeliveryDate}>
              <FormattedDate
                value={lineItem?.sane.finalDeliveryDate}
                weekday="long"
                day="numeric"
                month="long"
                year="numeric"
              >
                {lineItem?.sane.finalDeliveryDate}
              </FormattedDate>
            </time>
          </li>
        )}
      </ul>
      <hr />
      <footer className="pd-flex pd-flex-row pd-items-center pd-justify-end pd-gap-4">
        {canReOrder?.enabled && (
          <RecentOrderReOrderLink
            lineItem={lineItem}
            order={order}
            position={position}
            stateName={stateName}
          />
        )}
        <Button
          as={GatsbyLink}
          to={`account/orders/${order.id}?lineItemNumber=${lineItem.custom.fields.lineItemNumber}`}
          data-cy="orderListItemLink"
          variant="secondary"
          onClick={handleOrderDetailsClick}
        >
          <FormattedMessage
            id={recentOrderMessages.recentOrderOrderDetailsButton.id}
            defaultMessage={recentOrderMessages.recentOrderOrderDetailsButton.defaultMessage}
          />
        </Button>
      </footer>
    </Panel>
  );
};
